import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  ChangeLineDataFunc,
  TCommunitySavingGoals,
  THSChangedLinesItem,
  THSChangedLinesItemType,
  THSSavingGoalsParams,
  THSSavingGoalsRequestData,
  THSSavingGoalsTable
} from '../types'

import { setLoading } from '../../../redux/store/common/slice'
import { DEFAULT_PAGE } from '../../../components/Table/constants'
import { useDispatch } from 'react-redux'
import { notification } from '../../../components/Notification'
import moment, { Moment } from 'moment'
import { getSavingGoalsData, saveSavingGoalsData } from '../api'
import { VALIDATION_MESSAGES } from '../../../constants'
import { ACTIONS, SUBJECTS } from '../Abilities'
import { useSavingGoalsAbility } from './SavingGoalsAbilityProvider'

type ContextProps = {
  state: {
    changedHS: THSChangedLinesItem[]
    isSaveActive: boolean
    hsTableData: THSSavingGoalsTable[]
    hsTotalNumber: number
    pageInfo: { pageSize: number; pageNumber: number }
    community: TCommunitySavingGoals | null
    disableTableActions: boolean
    period: Moment
    highlightErrors?: boolean
    canEditHSSavingGoals: boolean
    canSeeCommunityTable: boolean
    canSeeHistory: boolean
  }
  actions: {
    onChangeLineData: ChangeLineDataFunc
    onCancelEditing: () => void
    handleTableChange: (pagination, _filters, sorter) => void
    handleChangePageSize: (pageSize: number) => void
    setCommunitySavingGoal: (savingGoal: number) => void
    onSaveAllData: () => void
    setPeriod: (period: Moment) => void
  }
}

const SavingGoalsContext = createContext<ContextProps>({
  state: {} as ContextProps['state'],
  actions: {} as ContextProps['actions']
})

const SavingGoalsContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch()

  const [sortParams, setSortParams] = useState<THSSavingGoalsRequestData>({})
  const [totalNumber, setTotalNumber] = useState(70)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const [changedHS, setChangedHS] = useState<THSChangedLinesItem[]>([])
  const [isSaveActive, setIsSaveActive] = useState<boolean>(false)
  const [hsTableData, setHsTableData] = useState<THSSavingGoalsTable[]>([])
  const [period, setPeriod] = useState<Moment>(moment())
  const [highlightErrors, setHighlightErrors] = useState<boolean>(false)

  const [community, setCommunity] = useState<TCommunitySavingGoals | null>(null)

  const ability = useSavingGoalsAbility()

  const canSeeCommunityTable = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.SAVINGS_GOALS_COMMUNITY),
    [ability]
  )

  // all users who have ability to see this page have view permission, but not all have edit permission
  const canEditHSSavingGoals = useMemo(
    () => ability.can(ACTIONS.EDIT, SUBJECTS.SAVINGS_GOALS_HS),
    [ability]
  )

  const canSeeHistory = useMemo(
    () => ability.can(ACTIONS.VIEW, SUBJECTS.SAVINGS_GOALS_HISTORY),
    [ability]
  )

  const getTableData = useCallback(
    async ({ sortField, sortOrder, page }: THSSavingGoalsRequestData) => {
      dispatch(setLoading(true))
      const params: THSSavingGoalsParams = {}
      if (sortField) {
        if (sortOrder) {
          params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
        }
        setSortParams({
          sortField,
          sortOrder
        })
      }
      const dataPage = page ? page : pageInfo
      params.limit = dataPage.pageSize
      params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
      const { data } = await getSavingGoalsData({
        ...params,
        year: period.year()
      })
      if (data) {
        setHsTableData(
          data.health_system_set.map((tableDataItem) => ({
            can_be_edited: tableDataItem.can_be_edited,
            name: tableDataItem.name,
            uuid: tableDataItem.uuid,
            savings_goal: tableDataItem.savings_goal,
            key: tableDataItem.uuid,
            has_history_records: tableDataItem.has_history_records,
            children: tableDataItem.hospital_set.map((hospital) => ({
              can_be_edited: tableDataItem.can_be_edited,
              name: hospital.name,
              uuid: hospital.uuid,
              savings_goal: hospital.savings_goal,
              hs_uuid: tableDataItem.uuid,
              has_history_records: hospital.has_history_records
            }))
          }))
        )
        setCommunity(data.community)
        setTotalNumber(data.health_system_total)
      }
      dispatch(setLoading(false))
    },
    [dispatch, pageInfo, period]
  )

  const handleTableChange = useCallback(
    (pagination, _filters, sorter) => {
      const page = {
        pageNumber: pagination.current,
        pageSize: pagination.pageSize
      }
      if (page.pageNumber !== pageInfo.pageNumber && changedHS.length) {
        notification.error({
          message: VALIDATION_MESSAGES.SAVING_GOALS_PAGINATION_DISABLED
        })
        return
      }
      getTableData({
        sortField: sorter.field,
        sortOrder: sorter.order,
        page
      })
      setPageInfo(page)
    },
    [pageInfo, changedHS, getTableData]
  )

  const handleChangePageSize = useCallback(
    (pageSize) => {
      if (changedHS.length) {
        notification.error({
          message: VALIDATION_MESSAGES.SAVING_GOALS_PAGINATION_DISABLED
        })
        return
      }
      const page = {
        pageNumber: 1,
        pageSize
      }
      getTableData({
        ...sortParams,
        page
      })
      setPageInfo(page)
    },
    [sortParams, getTableData, changedHS]
  )

  useEffect(() => {
    getTableData({ ...sortParams, page: pageInfo })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period])

  const checkError = useCallback(
    ({ uuid, hs_uuid }, value) => {
      if (hs_uuid) {
        // if hospital value was changed
        const hs = hsTableData.find((hsItem) => hsItem.uuid === hs_uuid)
        const hospitalsSum =
          hs?.children.reduce((acc, hospital) => {
            return (
              acc +
              (hospital.uuid === uuid ? value : Number(hospital.savings_goal))
            )
          }, 0) ?? 0
        return hospitalsSum > Number(hs?.savings_goal)
      } else {
        // if HS value was changed
        const hs = hsTableData.find((hsItem) => hsItem.uuid === uuid)
        const hospitalsSum =
          hs?.children.reduce((acc, hospital) => {
            return acc + Number(hospital.savings_goal)
          }, 0) ?? 0
        return hospitalsSum > Number(value)
      }
    },
    [hsTableData]
  )

  const updateChangedFields = useCallback(
    (changedField, type: THSChangedLinesItemType) => {
      // update changed HS / hospitals/ community array
      if (
        changedHS.find(
          (line) => line.type === type && line.uuid === changedField.uuid
        )
      ) {
        setChangedHS(
          changedHS.map((line) => {
            if (line.type === type && line.uuid === changedField.uuid)
              return { ...changedField, type }
            else return line
          })
        )
      } else {
        setChangedHS([...changedHS, { ...changedField, type }])
      }
    },
    [changedHS]
  )

  const onChangeLineData: ChangeLineDataFunc = useCallback(
    ({ uuid, hs_uuid }: { uuid: string; hs_uuid?: string }, value: string) => {
      setIsSaveActive(true)
      // find changed row
      const changedField = hs_uuid
        ? hsTableData
            .find((hsTableDataItem) => hsTableDataItem.uuid === hs_uuid)
            ?.children.find((hospitalItem) => hospitalItem.uuid === uuid)
        : hsTableData.find((hsTableDataItem) => hsTableDataItem.uuid === uuid)
      const error = checkError({ uuid, hs_uuid }, value)
      // set new table data (with changed value)
      setHsTableData(
        hsTableData.map((hsTableDataItem) => {
          if (hs_uuid) {
            if (hsTableDataItem.uuid === hs_uuid) {
              return {
                ...hsTableDataItem,
                error,
                children: hsTableDataItem.children.map(
                  (hsTableDataItemHospital) => {
                    if (hsTableDataItemHospital.uuid === uuid) {
                      return {
                        ...hsTableDataItemHospital,
                        savings_goal: value,
                        changed: true
                      }
                    } else return hsTableDataItemHospital
                  }
                )
              }
            } else return hsTableDataItem
          } else {
            if (hsTableDataItem.uuid === uuid) {
              return {
                ...hsTableDataItem,
                error,
                changed: true,
                savings_goal: value
              }
            } else return hsTableDataItem
          }
        })
      )

      if (changedField) {
        updateChangedFields(
          { ...changedField, savings_goal: value },
          hs_uuid ? 'hospital' : 'health_system'
        )
      }
    },
    [setIsSaveActive, hsTableData, checkError, updateChangedFields]
  )

  const onCancelEditing = useCallback(() => {
    setIsSaveActive(false)
    setChangedHS([])
    setHighlightErrors(false)
    getTableData({ ...sortParams })
  }, [getTableData, sortParams])

  const onSaveAllData = useCallback(async () => {
    if (hsTableData.find((hs) => !!hs.error)) {
      notification.error({ message: VALIDATION_MESSAGES.SM0209 })
      setHighlightErrors(true)
      return
    }
    try {
      dispatch(setLoading(true))
      const data = {
        items: [
          ...changedHS.map(({ uuid, savings_goal, type }) => ({
            community_unit_id: uuid,
            community_unit_type: type,
            savings_goal: savings_goal ?? 0
          }))
        ]
      }
      await saveSavingGoalsData(data, { year: period.year() })
      notification.success({
        message: VALIDATION_MESSAGES.SAVING_GOALS_SUCCESS
      })
      onCancelEditing()
    } catch (err) {
    } finally {
      dispatch(setLoading(false))
    }
  }, [hsTableData, changedHS, period, onCancelEditing, dispatch])

  const setCommunitySavingGoal = useCallback(
    (value: number) => {
      if (community) {
        setCommunity({
          ...community,
          changed: true,
          savings_goal: value
        })
        updateChangedFields({ ...community, savings_goal: value }, 'community')
        setIsSaveActive(true)
      }
    },
    [community, updateChangedFields]
  )

  const context = useMemo(
    () => ({
      state: {
        changedHS,
        isSaveActive,
        hsTableData,
        hsTotalNumber: totalNumber,
        pageInfo,
        community,
        disableTableActions: changedHS.length > 0,
        period,
        highlightErrors,
        canEditHSSavingGoals,
        canSeeCommunityTable,
        canSeeHistory
      },
      actions: {
        onChangeLineData,
        onCancelEditing,
        handleTableChange,
        handleChangePageSize,
        setCommunitySavingGoal,
        onSaveAllData,
        setPeriod
      }
    }),
    [
      changedHS,
      isSaveActive,
      onCancelEditing,
      onChangeLineData,
      hsTableData,
      totalNumber,
      handleTableChange,
      handleChangePageSize,
      pageInfo,
      community,
      setCommunitySavingGoal,
      onSaveAllData,
      period,
      setPeriod,
      highlightErrors,
      canEditHSSavingGoals,
      canSeeCommunityTable,
      canSeeHistory
    ]
  )

  return (
    <SavingGoalsContext.Provider value={context}>
      {children}
    </SavingGoalsContext.Provider>
  )
}

export const useSavingGoalsContext = () => useContext(SavingGoalsContext)

export default SavingGoalsContextProvider
